import { type ButtonHTMLAttributes, type CSSProperties, type ElementType } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'

const variants = cva(
  'w-fit inline-flex items-center justify-center whitespace-nowrap shrink-0 rounded-full text-13 font-medium ring-offset-background transition-colors duration-300 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      size: {
        default: 'h-8 px-4 text-13',
        large: 'h-10 px-6 text-16',
        mobile: 'h-11 w-full text-16 justify-center font-semibold',
      },
      style: {
        default:
          'font-semibold bg-dark-950 text-white hover:bg-dark-900 border border-dark-950 dark:bg-white dark:hover:bg-white/90 dark:text-black',
        invert: 'font-semibold bg-white text-950 border border-dark-50',
        gradient:
          'font-semibold border border-white/20 bg-gradient-to-b from-white to-white/80 text-black hover:bg-white',
        primary: 'font-semibold bg-primary-500 text-white hover:bg-primary-600',
        secondary:
          'font-semibold bg-primary-50 text-primary-500 hover:bg-primary-100 [&>div>svg>path]:stroke-primary-500',
        outline:
          'text-gray-950 font-semibold bg-white/50 border border-dark-100 backdrop-blur-md hover:bg-gray-50 dark:bg-black/50 dark:border-white/20 dark:text-white dark:hover:bg-black/75',
        outlinePrimary:
          'text-primary-500 bg-white dark:bg-black dark:border-white/15 dark:hover:border-white/30 border border-gray-200 hover:border-gray-300',
        glass: 'font-semibold backdrop-blur-md border',
        backgroundImage:
          'group font-semibold relative ml-0.5 bg-cover bg-center bg-no-repeat outline outline-2 outline-primary-500/20 transition-all before:absolute before:left-0 before:top-0 before:z-0 before:h-full before:w-full before:rounded-full before:bg-black/30 before:transition-colors before:duration-300 after:absolute after:left-0 after:top-0 after:z-10 after:h-full after:w-full after:rounded-full after:bg-primary-500/20 after:transition-colors after:duration-300 hover:outline-white/20 hover:before:bg-dark-950/30 hover:after:bg-primary-500/0',
        subtle: 'font-normal border bg-white/5 border-white/10 hover:bg-white/10 text-white',
        disabled: 'font-semibold bg-dark-100 text-dark-300 pointer-events-none',
      },
      theme: {
        dark: '',
        light: '',
      },
      icon: {
        true: 'pl-1 pr-3.5 flex items-center gap-1.5 whitespace-nowrap',
      },
      shadow: {
        true: 'shadow-button',
        false: '',
      },
      shine: {
        true: 'relative overflow-hidden',
      },
    },
    compoundVariants: [
      {
        style: 'default',
        theme: 'dark',
        class: 'bg-white/10 border-white/15 hover:bg-white/15 hover:border-white/20',
      },
      {
        style: 'outline',
        theme: 'dark',
        class: 'text-white bg-black border-white/15 hover:bg-black/75',
      },
      {
        style: 'outlinePrimary',
        theme: 'dark',
        class: 'text-primary-500 bg-black border-white/15 hover:border-primary-500/50',
      },
      {
        style: 'glass',
        theme: 'dark',
        class: 'text-white bg-white/10 border-white/15 hover:border-white/25',
      },
      {
        style: 'glass',
        theme: 'light',
        class: 'text-black bg-black/10 border-black/10 hover:border-black/20',
      },
    ],
    defaultVariants: {
      size: 'default',
      style: 'default',
    },
  },
)

const arrows = cva('rotate-180 ml-1.5', {
  variants: {
    style: {
      default: 'text-white',
      invert: 'text-gray-950',
      gradient: 'text-gray-400',
      primary: 'text-white',
      secondary: 'text-primary-300',
      outline: 'text-gray-950',
      outlinePrimary: 'text-primary-500',
      backgroundImage: '',
      subtle: 'text-white',
      disabled: 'text-dark-300',
      glass: 'text-white',
    },
  },
})

type PillProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof variants> &
  VariantProps<typeof arrows> & {
    size?: 'default' | 'large' | 'mobile'
    style?:
      | 'default'
      | 'invert'
      | 'gradient'
      | 'alternate'
      | 'primary'
      | 'secondary'
      | 'outline'
      | 'outlinePrimary'
      | 'search'
      | 'backgroundImage'
      | 'disabled'
      | 'subtle'
      | 'glass'
    custom?: string
    theme?: 'dark' | 'light'
    icon?: boolean
    arrow?: boolean
    chevron?: boolean
    background?: string
    shadow?: boolean
    shine?: boolean
    customStyles?: CSSProperties
    as?: 'button' | 'span' | 'div'
  }

export default function Pill({
  size,
  style,
  theme,
  icon,
  arrow,
  chevron,
  background,
  shadow,
  shine,
  custom,
  className,
  customStyles,
  as = 'button',
  ...props
}: PillProps) {
  const Component = as as ElementType
  return (
    <Component
      style={{
        backgroundImage: background && `url(${background})`,
        background: custom,
        ...customStyles,
      }}
      className={cn(variants({ size, style, theme, icon, shadow, shine, className }))}
      {...props}
      aria-hidden={as === 'span' || as === 'div' ? true : undefined}
    >
      {shine && (
        <span
          aria-hidden="true"
          style={{ animationIterationCount: 4, animationFillMode: 'forwards' }}
          className="pointer-events-none absolute inset-y-0 w-full bg-gradient-to-r from-transparent via-white/30 to-transparent motion-safe:animate-pill-shine dark:via-primary-500/30"
        />
      )}
      {props.children}
      {(arrow || chevron) && (
        <span className={cn(arrows({ style }))}>
          {arrow && <Icon name="arrow-left" />}
          {chevron && <Icon name="chevron-left" size={12} />}
        </span>
      )}
    </Component>
  )
}
